/*
  ЦИФРОВОЙ ДИАК ЗФ — AGILE RISK MANAGEMENT PANEL
  Create, view, edit, delete risks with probability × impact matrix.
  Risk status lifecycle: open → mitigated / accepted / closed.
*/

import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle, Shield, CheckCircle2, XCircle } from 'lucide-react';
import { type AgileRisk, riskColor, riskLabel, riskScore } from '@/store/projectStore';
import { useProjects } from '@/contexts/ProjectContext';
import { InlineText, InlineSelect } from './InlineEdit';
import { cn } from '@/lib/utils';

const PROB_OPTIONS = [
  { value: 'high', label: 'Высокая' },
  { value: 'medium', label: 'Средняя' },
  { value: 'low', label: 'Низкая' },
] as const;

const IMPACT_OPTIONS = [
  { value: 'high', label: 'Высокий' },
  { value: 'medium', label: 'Средний' },
  { value: 'low', label: 'Низкий' },
] as const;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Открыт' },
  { value: 'mitigated', label: 'Смягчён' },
  { value: 'accepted', label: 'Принят' },
  { value: 'closed', label: 'Закрыт' },
] as const;

function StatusIcon({ status }: { status: AgileRisk['status'] }) {
  if (status === 'open') return <AlertTriangle size={14} className="text-amber-500" />;
  if (status === 'mitigated') return <Shield size={14} className="text-blue-500" />;
  if (status === 'accepted') return <CheckCircle2 size={14} className="text-green-600" />;
  return <XCircle size={14} className="text-gray-400" />;
}

function RiskMatrix({ risks }: { risks: AgileRisk[] }) {
  const cells = ['high', 'medium', 'low'] as const;
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Матрица рисков</p>
      <div className="grid grid-cols-4 gap-0.5 text-xs">
        <div className="col-span-1" />
        {cells.map(c => (
          <div key={c} className="text-center font-semibold py-1 bg-muted rounded-sm">
            {c === 'high' ? 'Высокий' : c === 'medium' ? 'Средний' : 'Низкий'}
            <div className="text-[10px] text-muted-foreground font-normal">Влияние</div>
          </div>
        ))}
        {cells.map(prob => (
          <React.Fragment key={prob}>
            <div className="flex items-center justify-end pr-2 font-semibold text-[10px] text-muted-foreground">
              {prob === 'high' ? 'Высокая' : prob === 'medium' ? 'Средняя' : 'Низкая'}
              <div className="text-[9px] ml-0.5">(вер.)</div>
            </div>
            {cells.map(impact => {
              const score = { high: 3, medium: 2, low: 1 }[prob] * { high: 3, medium: 2, low: 1 }[impact];
              const count = risks.filter(r => r.probability === prob && r.impact === impact && r.status === 'open').length;
              const bg = score >= 6 ? 'bg-red-100 border-red-300' : score >= 3 ? 'bg-amber-100 border-amber-300' : 'bg-green-100 border-green-300';
              return (
                <div key={impact} className={cn('border rounded-sm flex items-center justify-center h-8', bg)}>
                  {count > 0 && (
                    <span className={cn('font-bold text-sm', score >= 6 ? 'text-red-700' : score >= 3 ? 'text-amber-700' : 'text-green-700')}>
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

interface RiskCardProps {
  risk: AgileRisk;
  onUpdate: (patch: Partial<AgileRisk>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function RiskCard({ risk, onUpdate, onDelete, readOnly: ro }: RiskCardProps) {
  const [expanded, setExpanded] = useState(false);
  const color = riskColor(risk);
  const label = riskLabel(risk);

  return (
    <div className="border rounded-lg overflow-hidden mb-2 bg-card shadow-sm">
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="w-1 self-stretch rounded-full shrink-0" style={{ backgroundColor: color }} />
        <StatusIcon status={risk.status} />
        <div className="flex-1 min-w-0">
          <InlineText
            value={risk.title}
            onSave={v => onUpdate({ title: v })}
            className="font-medium text-sm"
            displayAs="span"
            readOnly={ro}
          />
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: color + '22', color }}>
          {label}
        </span>
        <InlineSelect
          value={risk.status}
          options={STATUS_OPTIONS as unknown as { value: AgileRisk['status']; label: string }[]}
          onSave={v => onUpdate({ status: v })}
          readOnly={ro}
          renderValue={v => (
            <span className="text-xs text-muted-foreground">{STATUS_OPTIONS.find(o => o.value === v)?.label}</span>
          )}
        />
        {expanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t bg-muted/20 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Вероятность</p>
              <InlineSelect
                value={risk.probability}
                options={PROB_OPTIONS as unknown as { value: AgileRisk['probability']; label: string }[]}
                onSave={v => onUpdate({ probability: v })}
                readOnly={ro}
                renderValue={v => <span className="text-sm font-medium">{PROB_OPTIONS.find(o => o.value === v)?.label}</span>}
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Влияние</p>
              <InlineSelect
                value={risk.impact}
                options={IMPACT_OPTIONS as unknown as { value: AgileRisk['impact']; label: string }[]}
                onSave={v => onUpdate({ impact: v })}
                readOnly={ro}
                renderValue={v => <span className="text-sm font-medium">{IMPACT_OPTIONS.find(o => o.value === v)?.label}</span>}
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Владелец</p>
              <InlineText value={risk.owner} onSave={v => onUpdate({ owner: v })} placeholder="Ответственный" className="text-sm" readOnly={ro} />
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Описание риска</p>
            <InlineText value={risk.description} onSave={v => onUpdate({ description: v })} multiline placeholder="Описание…" className="text-sm w-full" readOnly={ro} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Стратегия митигации</p>
            <InlineText value={risk.mitigation} onSave={v => onUpdate({ mitigation: v })} multiline placeholder="Как снижаем риск…" className="text-sm w-full" readOnly={ro} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Резервный план (contingency)</p>
            <InlineText value={risk.contingency} onSave={v => onUpdate({ contingency: v })} multiline placeholder="Что делаем если риск реализовался…" className="text-sm w-full" readOnly={ro} />
          </div>
          {!ro ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const label = risk.title?.trim() || 'без названия';
                  const ok = window.confirm(`Удалить риск «${label}»? Действие необратимо.`);
                  if (!ok) return;
                }
                onDelete();
              }}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"
            >
              <Trash2 size={12} /> Удалить риск
            </button>
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

interface RiskPanelProps {
  projectId: string;
  risks: AgileRisk[];
  onAdd: (risk: Omit<AgileRisk, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdate: (riskId: string, patch: Partial<AgileRisk>) => void;
  onDelete: (riskId: string) => void;
}

export function RiskPanel({ projectId: _pid, risks, onAdd, onUpdate, onDelete }: RiskPanelProps) {
  const { canEdit } = useProjects();
  const ro = !canEdit;
  const openRisks = risks.filter(r => r.status === 'open');
  const closedRisks = risks.filter(r => r.status !== 'open');

  const handleAdd = () => {
    onAdd({
      title: 'Новый риск',
      description: '',
      probability: 'medium',
      impact: 'medium',
      status: 'open',
      owner: '',
      mitigation: '',
      contingency: '',
    });
  };

  return (
    <div>
      {risks.length > 0 && <RiskMatrix risks={risks} />}

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">Открытые риски ({openRisks.length})</span>
          {closedRisks.length > 0 && (
            <span className="text-xs text-muted-foreground">+ {closedRisks.length} закрытых</span>
          )}
        </div>
        {!ro ? (
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-[var(--nn-blue)] text-white hover:bg-[var(--nn-blue-dark)] transition-colors"
        >
          <Plus size={13} /> Добавить риск
        </button>
        ) : null}
      </div>

      {openRisks.map(r => (
        <RiskCard key={r.id} risk={r} onUpdate={p => onUpdate(r.id, p)} onDelete={() => onDelete(r.id)} readOnly={ro} />
      ))}

      {closedRisks.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
            Показать закрытые ({closedRisks.length})
          </summary>
          <div className="mt-2">
            {closedRisks.map(r => (
              <RiskCard key={r.id} risk={r} onUpdate={p => onUpdate(r.id, p)} onDelete={() => onDelete(r.id)} readOnly={ro} />
            ))}
          </div>
        </details>
      )}

      {risks.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-lg">
          Рисков пока нет. Нажмите «Добавить риск» чтобы начать.
        </div>
      )}
    </div>
  );
}
